import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
import FlexRow from "../../core/FlexRow";
import { WHITE } from "../../consts/color";
import ColumnSeparator from "../../core/ColumnSeparator";
import FlexColumn from "../../core/FlexColumn";
import Separator from "../../core/Separator";
import Completed from "../../core/Completed";
import TranslatedText from "../../core/TranslatedText";
import UnitTypeIcon from "../unit-list/UnitTypeIcon";
import UnitButton from "../unit-list/UnitButton";
import UnitListItemColumnSeparator from "../unit-list/UnitListItemColumnSeparator";
import UnitTypeAbilities from "../unit-list/UnitTypeAbilities";
import UnitReward from "../unit-list/UnitReward";
import UnitGrammars from "./UnitGrammars";
import moment from "moment";
import Icon from "../../core/Icon";

function HomeWorkListItemUnitCard({
  id,
  unit,
  executionResult,
  history,
  match,
  lastExecution,
  timeToAllowContinueUnit,
}) {
  const [isContinueAllowed, setIsContinueAllowed] = useState(false);

  const continueAllowedTimeoutRef = useRef(null);

  const isCompleted = useMemo(() => {
    return !!executionResult?.completedAt;
  }, [executionResult]);

  const unitPoints = useMemo(() => {
    if (isCompleted) {
      return executionResult?.rankingPoints;
    }
    if (unit.defaultFinishedAt && unit.firstReviewFinishedAt) {
      return 10;
    }
    if (unit.defaultFinishedAt && !unit.firstReviewFinishedAt) {
      return unit.firstReviewPoints;
    }
    return unit.defaultPoints;
  }, [unit, isCompleted, executionResult]);

  const isDisabled = useMemo(() => {
    return false;
  }, []);

  const unitButtonType = useMemo(() => {
    if (isContinueAllowed) {
      return "CONTINUE";
    }
    if (isDisabled) {
      return "LOCKED";
    }
    if (isCompleted) {
      return "RECAP_HOMEWORK";
    }
    return "START";
  }, [isCompleted, isDisabled, isContinueAllowed]);

  const handleStartUnitExecution = useCallback(() => {
    history.replace({
      pathname: `${match.url}/${id}/modules/${unit.module}/units/${unit.id}/exercise`,
      state: { fromHomework: true },
    });
  }, [match.url, unit.id, id]);

  useEffect(() => {
    if (lastExecution && lastExecution.lastActionAt && !lastExecution.completedAt) {
      if (moment().diff(moment(lastExecution.lastActionAt), "minutes", true) <= timeToAllowContinueUnit) {
        setIsContinueAllowed(true);
      }
    }
  }, [lastExecution?.lastActionAt]);

  useEffect(() => {
    if (isContinueAllowed) {
      const diff = moment().diff(
        moment(lastExecution?.lastActionAt).add(lastExecution?.studiedTime, "seconds"),
        "minutes",
        true
      );
      if (continueAllowedTimeoutRef.current) {
        clearTimeout(continueAllowedTimeoutRef.current);
      }
      continueAllowedTimeoutRef.current = setTimeout(() => {
        setIsContinueAllowed(false);
      }, 14.5 * 60 * 1000 - diff * 60 * 1000);
    }
  }, [isContinueAllowed, lastExecution?.lastActionAt]);

  return (
    <FlexColumn
      role="listitem"
      aria-labelledby={`unit-${unit.id}`}
      id={`unit-${unit.id}`}
      position="relative"
      backgroundColor={WHITE}
      padding="20px 15px"
      borderRadius={5}
      boxShadow="0px 2px 10px rgba(0, 0, 0, 0.1)"
      border={isCompleted ? "1px solid #0FB1A2" : "none"}
      width="100%"
    >
      <FlexRow>
        {isDisabled && (
          <FlexColumn
            top={0}
            left={0}
            width="100%"
            height="100%"
            position="absolute"
            backgroundColor="#ffffffcc"
            zIndex={5}
            justifyContent="center"
            alignItems="center"
          >
            <FlexColumn
              width={64}
              height={64}
              borderRadius={64}
              backgroundColor="#ECECEC"
              justifyContent="center"
              alignItems="center"
              marginTop={20}
            >
              <Icon color="#42424280" icon="lock-outline" size="sm" />
            </FlexColumn>
          </FlexColumn>
        )}
        <FlexRow flex={1} alignItems="center" paddingRight={10}>
          <UnitTypeIcon type={unit.type.id} isDisabled={isDisabled} isReview={false} />
          <ColumnSeparator size="xs" />
          <ColumnSeparator size="sm" />
          <FlexColumn alignItems="flex-start">
            <FlexRow alignItems="center">
              {isCompleted && (
                <React.Fragment>
                  <Completed />
                  <ColumnSeparator size="xs" />
                </React.Fragment>
              )}
              <FlexColumn>
                <FlexRow gap={4}>
                  <TranslatedText
                    translateKey="unit.steps.exercise"
                    style={{ fontWeight: "bold", fontSize: 12, color: "#424242" }}
                  />
                </FlexRow>
                <h1
                  style={{
                    margin: 0,
                    fontSize: 16,
                    lineHeight: "18px",
                    fontWeight: "bold",
                    color: "#424242",
                  }}
                >
                  {unit.name}
                </h1>
              </FlexColumn>
            </FlexRow>
            <Separator size="xs" />
            <UnitButton
              onClick={handleStartUnitExecution}
              isDisabled={isDisabled}
              isReview={false}
              type={unitButtonType}
            />
          </FlexColumn>
        </FlexRow>
        <FlexRow>
          {unit.grammars?.length ? (
            <>
              <UnitListItemColumnSeparator />
              <UnitGrammars grammars={unit.grammars} />
            </>
          ) : null}
          <UnitListItemColumnSeparator />
          <UnitTypeAbilities unit={unit.id} type={unit.type.id} isDisabled={isDisabled} isReview={false} />
          <UnitListItemColumnSeparator />
          <UnitReward points={unitPoints} completed={isCompleted} />
        </FlexRow>
      </FlexRow>
    </FlexColumn>
  );
}

HomeWorkListItemUnitCard.propTypes = {
  unit: PropTypes.shape({
    name: PropTypes.string,
    defaultFinishedAt: PropTypes.string,
  }).isRequired,
  executionResult: PropTypes.shape({
    completedAt: PropTypes.string,
    rankingPoints: PropTypes.number,
  }),
  timeToAllowContinueUnit: PropTypes.number,
};

HomeWorkListItemUnitCard.defaultProps = {
  timeToAllowContinueUnit: 15,
};

export default withRouter(HomeWorkListItemUnitCard);
